import {
    bookmarkIdFactory,
    bookmarkIdZodSchema,
} from "@@/shared/bookmarks/bookmark-id";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { productIdZodSchema } from "@@/shared/products/product-id";
import { productTemplateIdZodSchema } from "@@/shared/products/templates/product-template-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";

const bookmarkOwnerTypeZodLiteral = z.enum(["provider", "user"]);
const bookmarkForZodUnion = z.union([
    productIdZodSchema,
    productTemplateIdZodSchema,
]);
const bookmarkScopeZodArray = z.array(
    z.union([providerIdZodSchema, z.string()]),
);
type BookmarkScope = z.infer<typeof bookmarkScopeZodArray>;
const bookmarkOwnerZodUnion = z.union([userIdZodSchema, providerIdZodSchema]);
const bookmarkTypeZodLiteral = z.enum(["PRODUCT", "PRODUCT_TEMPLATE"]);
type BookmarkType = z.infer<typeof bookmarkTypeZodLiteral>;
const bookmarkZodObject = z.object({
    _id: bookmarkIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _createdBy: userIdZodSchema,
    _type: z.literal("BOOKMARK"),
    /** The type of the bookmarked type */
    type: bookmarkTypeZodLiteral,
    /** The id of the bookmarked item */
    bookmarkedId: bookmarkForZodUnion,
    /** The type of owner, user or provider */
    ownerType: bookmarkOwnerTypeZodLiteral,
    /**
     * Who is the bookmark visible for?
     * What user or, if provider,
     * all users belonging to that provider.
     */
    ownerId: bookmarkOwnerZodUnion,
    /**
     * Does the bookmarked item belong to a specific provider?
     * If so, this should be set to the providerId it belongs to.
     */
    providerId: providerIdZodSchema.optional(),
    // Scope is used to filter bookmarks for a specific
    // part of the app, e.g. if set to a providerId
    // the bookmark is for the given providers back office
    scope: bookmarkScopeZodArray,
});
type Bookmark = z.infer<typeof bookmarkZodObject>;

type FactoryParams = Except<Bookmark, "_id" | "_createdAt" | "_type">;

const bookmarkFactory = (params: FactoryParams): Bookmark => {
    return {
        ...params,
        _id: bookmarkIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _type: "BOOKMARK",
    };
};

export {
    bookmarkFactory,
    bookmarkForZodUnion,
    bookmarkOwnerTypeZodLiteral,
    bookmarkOwnerZodUnion,
    bookmarkScopeZodArray,
    bookmarkTypeZodLiteral,
    bookmarkZodObject,
};
export type { Bookmark, BookmarkScope, BookmarkType };
